// ============================================================================
// PasswordResetPage — set a new password via emailed reset link (Task #2).
// ============================================================================
// Routed via #password-reset/<token>. Public — no bearer required. Validates
// the new password matches a confirmation field, then hits the confirm
// endpoint. On success, sends the user back to /#login.
// ============================================================================

import { useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle, KeyRound } from 'lucide-react';
import { api } from '../api';

interface Props {
  token: string;
}

export function PasswordResetPage({ token }: Props) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.'); return;
    }
    if (password !== confirm) {
      setError('The two passwords don\'t match.'); return;
    }
    setBusy(true); setError('');
    try {
      await api.confirmPasswordReset(token, password);
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Could not reset password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-12 md:h-14 w-auto" />
          </a>
        </div>
      </header>

      <main className="flex-1 max-w-md mx-auto px-4 py-12 w-full">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden p-6">
          {done ? (
            <div className="space-y-4 text-center">
              <CheckCircle2 className="h-12 w-12 text-uif-green mx-auto" />
              <h1 className="text-xl font-bold text-uif-green-dark">Password updated</h1>
              <p className="text-sm text-gray-700">
                You're all set. Sign in with your new password below.
              </p>
              <a
                href="#login"
                className="inline-block w-full rounded-md bg-uif-green text-white font-semibold px-4 py-2 hover:bg-uif-green-dark"
              >
                Go to sign in
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="text-center">
                <KeyRound className="h-10 w-10 text-uif-green-dark mx-auto mb-2" />
                <h1 className="text-xl font-bold text-uif-green-dark">Set a new password</h1>
                <p className="text-sm text-gray-600 mt-1">Pick at least 8 characters.</p>
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>{error}</div>
                </div>
              )}
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">New password</span>
                <input
                  type="password"
                  required
                  autoFocus
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  minLength={8}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</span>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={busy}
                  minLength={8}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                />
              </label>
              <button
                type="submit"
                disabled={busy || password.length < 8 || password !== confirm}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-4 py-3 hover:bg-uif-green-dark disabled:bg-gray-300"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                Save new password
              </button>
              <p className="text-[11px] text-gray-500 text-center">
                Reset links expire 1 hour after they're sent. If yours is expired, request a new one from the sign-in page.
              </p>
            </form>
          )}
        </div>
      </main>

      <footer className="border-t border-gray-200 bg-white py-6">
        <div className="max-w-2xl mx-auto px-4 text-center text-xs text-gray-500">
          Up in Farms LLC · <a href="mailto:support@upinfarms.com" className="text-uif-green hover:underline">support@upinfarms.com</a>
        </div>
      </footer>
    </div>
  );
}
